/**
 * ClearBookmarksDialog — the warning panel Synth VR shows before every bookmark is cleared (F8, or Clear All in
 * the bookmark list). Bookmarks are not part of the undo history, so a clear cannot be taken back, and nothing is
 * cleared until the mapper says so. The card takes the focus, never a button, so the keypress that opened it
 * cannot choose for the mapper; Escape keeps the bookmarks.
 */

import React, { useEffect, useRef } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { color, font, tint, ui, z } from '../ui/theme';

export default function ClearBookmarksDialog() {
  const pending = useEditorStore(s => s.clearBookmarksPending);
  const count = useEditorStore(s => s.bookmarks.length);
  const confirm = useEditorStore(s => s.confirmClearBookmarks);
  const cancel = useEditorStore(s => s.cancelClearBookmarks);

  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pending) cardRef.current?.focus();
  }, [pending]);

  // Capture phase and stopped: with the bookmark list open behind this panel, Escape must close this panel only.
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      cancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [pending, cancel]);

  if (!pending) return null;

  return (
    <div style={backdropStyle} onMouseDown={cancel}>
      <div ref={cardRef} tabIndex={-1} style={cardStyle} role="alertdialog" aria-label="Delete all bookmarks" onMouseDown={e => e.stopPropagation()}>
        <div style={titleBarStyle}>
          <div style={titleStyle}>Delete all bookmarks?</div>
        </div>
        <div style={bodyStyle}>
          <div style={textStyle}>
            This deletes {count === 1 ? 'the 1 bookmark' : `all ${count} bookmarks`} in this map.
          </div>
          <div style={warnStyle}>
            Bookmarks are not part of the undo history, so this cannot be undone.
          </div>
          <div style={rowStyle}>
            <button style={cancelBtnStyle} onClick={cancel}>Cancel</button>
            <button style={deleteBtnStyle} onClick={confirm}>Delete {count === 1 ? 'bookmark' : 'all bookmarks'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const backdropStyle: React.CSSProperties = { ...ui.overlay, zIndex: z.modal + 1 };

const cardStyle: React.CSSProperties = { ...ui.dialog, width: 400, maxWidth: '92vw', outline: 'none' };

const titleBarStyle: React.CSSProperties = { ...ui.dialogHeader, height: 44, padding: '0 16px' };

const titleStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: color.dangerStrong,
  fontFamily: font.sans, letterSpacing: 1.2, textTransform: 'uppercase',
};

const bodyStyle: React.CSSProperties = { padding: 18, display: 'flex', flexDirection: 'column', gap: 12 };

const textStyle: React.CSSProperties = { fontSize: 12, color: color.ink, fontFamily: font.sans, lineHeight: 1.5 };

const warnStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: color.danger, fontFamily: font.sans, lineHeight: 1.5 };

const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 };

const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  color: color.inkDim, fontFamily: font.sans, fontSize: 11, fontWeight: 600, cursor: 'pointer',
};

const deleteBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 999,
  border: `1px solid ${color.dangerStrong}`,
  background: tint.dangerLow,
  color: color.dangerStrong, fontFamily: font.sans, fontSize: 11, fontWeight: 700, cursor: 'pointer',
};
